import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { detailFromAxiosError, formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "@/pages/layoutChrome";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  FileCode,
  FileDown,
  HelpCircle,
  ListOrdered,
  Loader2,
  Plus,
  Save,
  Split,
  Trash2,
  Upload,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const FLUJO_OPERATIVO_CUATRIMESTRAL_DEFAULT = [
  {
    paso: 1,
    titulo: "Cliente, contrato REPSE, año y cuatrimestre",
    descripcion:
      "Seleccione el cliente (patrón), el contrato REPSE de la obra, el año fiscal y el cuatrimestre (1, 2 o 3).",
  },
  {
    paso: 2,
    titulo: "Descargar plantilla Excel",
    descripcion:
      "Use «Plantilla vacía» para captura masiva o «Con ejemplo» para ver el formato. Ideal para periodos anteriores al sistema.",
  },
  {
    paso: 3,
    titulo: "Llenar la plantilla",
    descripcion:
      "Complete NSS, SDI y días trabajados (obligatorios) por fila. Opcional: RFC, CURP, nombre, registro patronal y conceptos SUA.",
  },
  {
    paso: 4,
    titulo: "Importar Excel y revisar el grid",
    descripcion:
      "Suba el archivo con «Importar desde Excel / XML». Las celdas incompletas se resaltan en amarillo.",
  },
  {
    paso: 5,
    titulo: "Guardar en historial manual (opcional)",
    descripcion:
      "Pulse «Guardar en Historial Manual» para retomar el mismo lote más tarde.",
  },
  {
    paso: 6,
    titulo: "Generar layouts SISUB y/o ICSOE",
    descripcion:
      "Con todas las filas completas, descargue el archivo .txt SISUB (IMSS) y/o ICSOE (INFONAVIT) para envío.",
  },
];

const ORIGEN_CATALOGO = "catalogo";
const ORIGEN_MANUAL = "manual";
const ORIGEN_VISOR = "visor_sat";

const CAMPOS_SUA = [
  "dias_incapacidad",
  "dias_ausentismo",
  "aportacion_infonavit",
  "amortizacion_credito",
];

function emptyRow(extra = {}) {
  return {
    row_id: crypto.randomUUID?.() || `row-${Date.now()}-${Math.random()}`,
    trabajador_id: "",
    nss: "",
    rfc: "",
    curp: "",
    nombre_completo: "",
    sdi: "",
    dias_trabajados: "",
    registro_patronal: "",
    dias_incapacidad: "",
    dias_ausentismo: "",
    aportacion_infonavit: "",
    amortizacion_credito: "",
    contrato_id: "",
    origen: "",
    multi_contrato: false,
    contratos_periodo: [],
    campos_faltantes: [],
    ...extra,
  };
}

function cellClass(campo, row, editableIdentity) {
  const missing = Array.isArray(row.campos_faltantes) && row.campos_faltantes.includes(campo);
  if (missing) return "bg-amber-100 border-amber-300";
  if (!editableIdentity && ["nss", "rfc", "curp", "nombre_completo"].includes(campo)) {
    return "bg-slate-50";
  }
  return "";
}

async function downloadLayoutBlob({ endpoint, payload, fallbackFilename }) {
  const response = await api.post(endpoint, payload, {
    responseType: "blob",
    timeout: 120000,
  });
  const blob = response.data;
  if (!(blob instanceof Blob) || blob.size === 0) {
    throw new Error("Archivo generado vacío");
  }
  const cd = response.headers["content-disposition"] || response.headers["Content-Disposition"] || "";
  let filename = fallbackFilename;
  const quoted = cd.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) filename = quoted[1];
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 120000);
  return filename;
}

async function downloadPlantillaCuatrimestral(withExample) {
  const { data, headers } = await api.get("/laboral/confronta-cuatrimestral/plantilla-excel", {
    responseType: "blob",
    params: { with_example: withExample },
  });
  if (!(data instanceof Blob) || data.size === 0) {
    throw new Error("El servidor devolvió un archivo vacío");
  }
  const cd = headers["content-disposition"] || headers["Content-Disposition"] || "";
  let filename = withExample
    ? "plantilla_cuatrimestral_sisub_icsoe_ejemplo.xlsx"
    : "plantilla_cuatrimestral_sisub_icsoe.xlsx";
  const quoted = cd.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) filename = quoted[1];
  const url = URL.createObjectURL(data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 120000);
  return filename;
}

/**
 * Declaraciones cuatrimestrales SISUB / ICSOE con origen condicionado a Visor SAT.
 */
export default function ConfrontaTrabajadoresImss() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [config, setConfig] = useState(null);
  const [contratos, setContratos] = useState([]);
  const [contratoId, setContratoId] = useState("");
  const [anio, setAnio] = useState(() => new Date().getFullYear());
  const [cuatrimestre, setCuatrimestre] = useState("1");
  const [origen, setOrigen] = useState(ORIGEN_CATALOGO);
  const [filas, setFilas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [validating, setValidating] = useState(false);
  const [sincronizarTrabajadores, setSincronizarTrabajadores] = useState(false);
  const [dragOverPdf, setDragOverPdf] = useState(false);
  const excelInputRef = useRef(null);
  const xmlInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const visorSatActivo = Boolean(config?.modulo_visor_sat ?? config?.visor_sat_sync_disponible);
  const modoManual = origen === ORIGEN_MANUAL;
  const instrucciones = config?.instrucciones || "";
  const alertaIncompletos = config?.alerta_campos_incompletos || "";
  const cuatrimestres = config?.cuatrimestres || [];
  const modalidadMsg = config?.mensaje_modalidad || "";
  const flujoOperativo = config?.flujo_operativo?.length
    ? config.flujo_operativo
    : FLUJO_OPERATIVO_CUATRIMESTRAL_DEFAULT;
  const plantillaVersion = config?.plantilla_cuatrimestral_version || "1.0";

  const contratoSel = useMemo(
    () => contratos.find((c) => c.id === contratoId) || null,
    [contratos, contratoId],
  );

  const filasMultiContrato = useMemo(
    () => filas.filter((r) => r.multi_contrato && (r.contratos_periodo?.length || 0) > 1),
    [filas],
  );

  const payloadBase = useMemo(
    () => ({
      client_id: clientId,
      contrato_id: contratoId,
      anio: Number(anio),
      cuatrimestre,
      items: filas.map(({ campos_faltantes, row_index, ...rest }) => rest),
    }),
    [clientId, contratoId, anio, cuatrimestre, filas],
  );

  const loadContratos = useCallback(async () => {
    if (!clientId) {
      setContratos([]);
      return;
    }
    try {
      const { data } = await api.get("/laboral/contratos-repse", { params: { client_id: clientId } });
      setContratos(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setContratos([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar contratos REPSE");
    }
  }, [clientId]);

  const loadCatalogoAsignados = useCallback(async () => {
    if (!clientId || !contratoId || origen !== ORIGEN_CATALOGO) return;
    setBusy(true);
    try {
      const { data } = await api.get("/laboral/confronta-cuatrimestral/trabajadores-automaticos", {
        params: { client_id: clientId, contrato_id: contratoId, anio, cuatrimestre },
      });
      setFilas((data.items || []).map((r) => ({ ...r, campos_faltantes: [] })));
    } catch (e) {
      setFilas([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar trabajadores");
    } finally {
      setBusy(false);
    }
  }, [clientId, contratoId, anio, cuatrimestre, origen]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setConfig(null);
      return;
    }
    api
      .get("/laboral/confronta-cuatrimestral/config", { params: { client_id: clientId } })
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig(null));
  }, [clientId]);

  useEffect(() => {
    loadContratos();
  }, [loadContratos]);

  useEffect(() => {
    setContratoId("");
    setFilas([]);
  }, [clientId]);

  useEffect(() => {
    if (origen === ORIGEN_CATALOGO && contratoId && !visorSatActivo) {
      loadCatalogoAsignados();
    }
  }, [origen, contratoId, anio, cuatrimestre, visorSatActivo, loadCatalogoAsignados]);

  const onOrigenManual = () => {
    setOrigen(ORIGEN_MANUAL);
    setFilas([]);
    if (contratoId && clientId) {
      api
        .get("/laboral/confronta-cuatrimestral/historial-manual", {
          params: { client_id: clientId, contrato_id: contratoId, anio, cuatrimestre },
        })
        .then(({ data }) => {
          if (data?.items?.length) {
            setFilas(data.items.map((r) => ({ ...r, campos_faltantes: [] })));
            toast.info("Se recuperó el último lote manual guardado para este periodo.");
          }
        })
        .catch(() => {});
    }
  };

  const updateFila = (rowId, campo, valor) => {
    setFilas((prev) =>
      prev.map((r) =>
        r.row_id === rowId
          ? { ...r, [campo]: valor, campos_faltantes: (r.campos_faltantes || []).filter((c) => c !== campo) }
          : r,
      ),
    );
  };

  const agregarFila = () => {
    setFilas((prev) => [
      ...prev,
      emptyRow({ registro_patronal: contratoSel?.registro_patronal || "", contrato_id: contratoId }),
    ]);
    setOrigen(ORIGEN_MANUAL);
  };

  const eliminarFila = (rowId) => {
    setFilas((prev) => prev.filter((r) => r.row_id !== rowId));
  };

  const enriquecerFilasImportadas = (items) =>
    (items || []).map((r) => ({
      ...r,
      registro_patronal: r.registro_patronal || contratoSel?.registro_patronal || "",
      contrato_id: r.contrato_id || contratoId,
      campos_faltantes: [],
    }));

  const onImportarExterno = async (excelFile, xmlFiles) => {
    if (!clientId || (!excelFile && (!xmlFiles || !xmlFiles.length))) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      if (excelFile) body.append("archivo_excel", excelFile);
      (xmlFiles || []).forEach((f) => body.append("archivos_xml", f));
      const { data } = await api.post("/laboral/confronta-cuatrimestral/importar-externo", body);
      const imported = enriquecerFilasImportadas(data.items);
      setFilas(imported);
      setOrigen(ORIGEN_MANUAL);
      toast.success(`Importadas ${imported.length} fila(s). Revise conceptos SUA antes de exportar.`);
    } catch (e) {
      toast.error(await detailFromAxiosError(e) || "No se pudo importar Excel/XML");
    } finally {
      setBusy(false);
      if (excelInputRef.current) excelInputRef.current.value = "";
      if (xmlInputRef.current) xmlInputRef.current.value = "";
    }
  };

  const onImportarPdfs = async (files) => {
    if (!clientId || !files?.length) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      Array.from(files).forEach((f) => body.append("archivos_pdf", f));
      const { data } = await api.post("/laboral/confronta-cuatrimestral/importar-pdfs", body);
      const imported = enriquecerFilasImportadas(data.items);
      setFilas((prev) => [...prev, ...imported]);
      setOrigen(ORIGEN_MANUAL);
      toast.success(`Extraídas ${imported.length} fila(s) de PDF(s). Complete NSS/SDI si faltan.`);
    } catch (e) {
      toast.error(await detailFromAxiosError(e) || "No se pudieron procesar los PDFs");
    } finally {
      setBusy(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const onDesglosarMultiContrato = async (modo) => {
    if (!filasMultiContrato.length) {
      toast.info("No hay filas con múltiples contratos en el periodo");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/laboral/confronta-cuatrimestral/desglosar-multi-contrato", {
        ...payloadBase,
        modo,
      });
      setFilas((data.items || []).map((r) => ({ ...r, campos_faltantes: [] })));
      toast.success(`Desglose ${modo}: ${data.count} fila(s)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo desglosar");
    } finally {
      setBusy(false);
    }
  };

  const onSincronizarTrabajadores = async () => {
    if (!clientId || !contratoId || filas.length === 0) {
      toast.error("No hay filas para sincronizar");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/laboral/confronta-cuatrimestral/sincronizar-trabajadores", payloadBase);
      toast.success(`Sincronizados ${data.updated_count || 0} trabajador(es) en ${data.periodo || "periodo"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo sincronizar");
    } finally {
      setBusy(false);
    }
  };

  const onGuardarManual = async () => {
    if (!clientId || !contratoId || !filas.length) {
      toast.warning("Seleccione contrato y agregue filas");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/laboral/confronta-cuatrimestral/guardar-manual", {
        ...payloadBase,
        sincronizar_trabajadores: sincronizarTrabajadores,
      });
      toast.success(data.message || "Historial manual guardado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  };

  const aplicarValidacion = (items) => {
    if (!Array.isArray(items)) return;
    setFilas((prev) =>
      prev.map((row, idx) => {
        const v = items.find((x) => x.row_index === idx) || items[idx];
        return v ? { ...row, ...v, campos_faltantes: v.campos_faltantes || [] } : row;
      }),
    );
  };

  const validarAntesExportar = async () => {
    if (!filas.length) {
      toast.warning("No hay trabajadores en la tabla");
      return false;
    }
    setValidating(true);
    try {
      const { data } = await api.post("/laboral/confronta-cuatrimestral/validar-filas", payloadBase);
      aplicarValidacion(data.items);
      if (!data.ok) {
        toast.error(data.message || alertaIncompletos, { duration: 8000 });
        return false;
      }
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al validar filas");
      return false;
    } finally {
      setValidating(false);
    }
  };

  const onDescargarPlantilla = async (withExample) => {
    setBusy(true);
    try {
      const filename = await downloadPlantillaCuatrimestral(withExample);
      toast.success(`Plantilla descargada: ${filename}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar la plantilla");
    } finally {
      setBusy(false);
    }
  };

  const onGenerarSisub = async () => {
    if (!contratoId) {
      toast.error("Seleccione un contrato REPSE");
      return;
    }
    const ok = await validarAntesExportar();
    if (!ok) return;
    setBusy(true);
    try {
      const filename = await downloadLayoutBlob({
        endpoint: "/laboral/confronta-cuatrimestral/generar-sisub",
        payload: payloadBase,
        fallbackFilename: `layout_sisub_${anio}_C${cuatrimestre}.txt`,
      });
      toast.success(`Layout SISUB descargado: ${filename}`);
    } catch (e) {
      const detail = e.response?.data;
      if (detail instanceof Blob) {
        const text = await detail.text();
        try {
          const json = JSON.parse(text);
          if (json.detail?.items) aplicarValidacion(json.detail.items);
          toast.error(json.detail?.message || alertaIncompletos, { duration: 8000 });
        } catch {
          toast.error("No se pudo generar SISUB");
        }
      } else {
        toast.error(await detailFromAxiosError(e) || "No se pudo generar SISUB");
      }
    } finally {
      setBusy(false);
    }
  };

  const onGenerarIcsoe = async () => {
    if (!contratoId) {
      toast.error("Seleccione un contrato REPSE");
      return;
    }
    const ok = await validarAntesExportar();
    if (!ok) return;
    setBusy(true);
    try {
      const filename = await downloadLayoutBlob({
        endpoint: "/laboral/confronta-cuatrimestral/generar-icsoe",
        payload: payloadBase,
        fallbackFilename: `layout_icsoe_${anio}_C${cuatrimestre}.txt`,
      });
      toast.success(`Layout ICSOE descargado: ${filename}`);
    } catch (e) {
      const detail = e.response?.data;
      if (detail instanceof Blob) {
        const text = await detail.text();
        try {
          const json = JSON.parse(text);
          if (json.detail?.items) aplicarValidacion(json.detail.items);
          toast.error(json.detail?.message || alertaIncompletos, { duration: 8000 });
        } catch {
          toast.error("No se pudo generar ICSOE");
        }
      } else {
        toast.error(await detailFromAxiosError(e) || "No se pudo generar ICSOE");
      }
    } finally {
      setBusy(false);
    }
  };

  const origenLabel = {
    [ORIGEN_VISOR]: "Visor SAT (CFDI nómina)",
    [ORIGEN_MANUAL]: "Carga externa / manual",
    [ORIGEN_CATALOGO]: "Catálogo asignado al contrato",
  }[origen] || origen;

  const renderCell = (row, campo, placeholder = "", forceEditable = false) => {
    const editableIdentity = modoManual || origen === ORIGEN_VISOR || forceEditable;
    const readOnly =
      !editableIdentity && !CAMPOS_SUA.includes(campo) && ["nss", "rfc", "curp", "nombre_completo", "registro_patronal"].includes(campo);
    return (
      <Input
        value={row[campo] || ""}
        readOnly={readOnly}
        onChange={(e) => updateFila(row.row_id, campo, e.target.value)}
        className={`h-8 text-[11px] font-mono ${cellClass(campo, row, editableIdentity)}`}
        placeholder={placeholder}
      />
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PageHeader
        title="Declaraciones cuatrimestrales"
        subtitle="SISUB (IMSS) e ICSOE (INFONAVIT) por obra y cuatrimestre."
        breadcrumb={["Asistente Laboral y REPSE", "Declaraciones cuatrimestrales"]}
      />

      <LaboralFlujoOperativo>
      <div className="flex-1 grid grid-cols-1 xl:grid-cols-[360px_1fr] min-h-0 border-t border-slate-200">
        <div className="overflow-y-auto p-4 space-y-4 bg-[#F4F6F8] border-b xl:border-b-0 xl:border-r border-slate-200">
          <Tabs defaultValue="acciones" className="space-y-4">
            <TabsList className="w-full grid grid-cols-2 h-9 bg-white border border-slate-200">
              <TabsTrigger value="acciones" className="text-xs data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white">
                Acciones
              </TabsTrigger>
              <TabsTrigger
                value="guia"
                className="text-xs data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white"
                data-testid="tab-guia-cuatrimestral"
              >
                <ListOrdered className="w-3.5 h-3.5 mr-1" />
                Guía del flujo
              </TabsTrigger>
            </TabsList>

            <TabsContent value="guia" className="mt-0 space-y-3">
              <div className="bg-white rounded-md border border-sky-200 p-4 space-y-3">
                <p className="text-xs font-semibold text-[#0F2D5C] m-0">
                  Flujo operativo — declaraciones cuatrimestrales
                </p>
                <p className="text-[11px] text-slate-600 m-0 leading-relaxed">
                  Siga estos pasos para periodos históricos o cuando aún no tiene los datos en el sistema.
                  Plantilla Excel v{plantillaVersion}.
                </p>
                <ol className="space-y-3 m-0 pl-0 list-none">
                  {flujoOperativo.map((paso) => (
                    <li key={paso.paso} className="flex gap-3 text-xs">
                      <span className="inline-flex w-6 h-6 shrink-0 items-center justify-center rounded-full bg-[#0F2D5C] text-white text-[10px] font-bold">
                        {paso.paso}
                      </span>
                      <div>
                        <p className="font-semibold text-slate-800 m-0">{paso.titulo}</p>
                        <p className="text-slate-600 m-0 mt-0.5 leading-relaxed">{paso.descripcion}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs justify-start"
                    disabled={busy}
                    onClick={() => onDescargarPlantilla(false)}
                    data-testid="btn-plantilla-cuatrimestral-vacia"
                  >
                    <FileDown className="w-3.5 h-3.5 mr-2" />
                    Descargar plantilla vacía
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 text-xs justify-start border-emerald-300 text-emerald-900"
                    disabled={busy}
                    onClick={() => onDescargarPlantilla(true)}
                    data-testid="btn-plantilla-cuatrimestral-ejemplo"
                  >
                    <FileDown className="w-3.5 h-3.5 mr-2" />
                    Descargar plantilla con ejemplo
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="acciones" className="mt-0 space-y-4">
          {/* Indicador modalidad Visor SAT */}
          <div
            className={`rounded-md border px-3 py-2.5 text-xs leading-snug ${
              visorSatActivo
                ? "border-emerald-300 bg-emerald-50/90 text-emerald-950"
                : "border-slate-300 bg-white text-slate-600"
            }`}
            data-testid="visor-sat-modalidad-indicator"
          >
            {visorSatActivo ? (
              <span>🟢 {modalidadMsg || "Conexión con Visor SAT: Activada (Modo Automático Disponible)"}</span>
            ) : (
              <span>⚪ {modalidadMsg || "Modo Local: Carga Externa (Adquiera Visor SAT para sincronización directa)"}</span>
            )}
          </div>

          {instrucciones ? (
            <div className="rounded-md border border-sky-200 bg-sky-50/90 px-3 py-3 text-xs text-slate-800 leading-relaxed flex gap-2">
              <HelpCircle className="w-4 h-4 shrink-0 text-sky-700 mt-0.5" />
              <p className="m-0">{instrucciones}</p>
            </div>
          ) : null}

          <div className="bg-white rounded-md border border-slate-200 p-4 space-y-3">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Cliente (patrón)</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="h-10" data-testid="confronta-client-select">
                <SelectValue placeholder="Seleccione cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.razon_social} — {c.rfc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-white rounded-md border border-slate-200 p-4 space-y-3">
            <Label className="text-xs font-semibold text-[#0F2D5C]">Contrato REPSE</Label>
            <Select value={contratoId} onValueChange={setContratoId} disabled={!clientId}>
              <SelectTrigger className="h-10 text-xs">
                <SelectValue placeholder={contratos.length ? "Seleccione contrato" : "Sin contratos — dé de alta en Control REPSE"} />
              </SelectTrigger>
              <SelectContent>
                {contratos.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.numero_contrato} — {(c.razon_social_obra || "Obra").slice(0, 40)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Año</Label>
                <Input
                  type="number"
                  min={2000}
                  max={2100}
                  value={anio}
                  onChange={(e) => setAnio(Number(e.target.value) || new Date().getFullYear())}
                  className="h-9 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-slate-500">Cuatrimestre</Label>
                <Select value={cuatrimestre} onValueChange={setCuatrimestre}>
                  <SelectTrigger className="h-9 text-xs mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(cuatrimestres.length ? cuatrimestres : [
                      { id: "1", label: "1er cuatrimestre" },
                      { id: "2", label: "2do cuatrimestre" },
                      { id: "3", label: "3er cuatrimestre" },
                    ]).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Carga externa o catálogo (alta desde Visor SAT → Catálogo de trabajadores) */}
          <div className={`bg-white rounded-md border border-slate-200 p-4 space-y-2 ${visorSatActivo ? "opacity-100" : ""}`}>
            <p className="text-[10px] text-slate-600 m-0">
              Para dar de alta trabajadores use{" "}
              <strong>Catálogo de trabajadores → Importar desde nómina</strong> (Visor SAT, Excel, XML o PDF).
              Aquí importe datos del cuatrimestre para SISUB/ICSOE.
            </p>
            <Label className="text-xs font-semibold text-[#0F2D5C]">Plantilla Excel (periodos históricos)</Label>
            <p className="text-[10px] text-slate-500 m-0">
              Descargue, llene NSS/SDI/días e impórtela abajo. Véase también la pestaña «Guía del flujo».
            </p>
            <div className="grid grid-cols-1 gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-10 justify-start text-xs font-semibold border-[#0F2D5C] text-[#0F2D5C]"
                disabled={busy}
                onClick={() => onDescargarPlantilla(false)}
                data-testid="btn-descargar-plantilla-vacia-sidebar"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Plantilla vacía (.xlsx)
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="h-9 justify-start text-xs"
                disabled={busy}
                onClick={() => onDescargarPlantilla(true)}
                data-testid="btn-descargar-plantilla-ejemplo-sidebar"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Plantilla con fila de ejemplo
              </Button>
            </div>
            <Label className="text-xs font-semibold text-[#0F2D5C] pt-2 block">Importar datos</Label>
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 justify-start font-semibold border-[#0F2D5C] text-[#0F2D5C]"
              disabled={busy}
              onClick={() => {
                excelInputRef.current?.click();
              }}
              data-testid="btn-importar-externo-excel"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <span className="mr-2">📥</span>}
              Importar desde Excel / XML
            </Button>
            <input
              ref={excelInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const excel = e.target.files?.[0] || null;
                if (excel) onImportarExterno(excel, null);
              }}
            />
            <input
              ref={xmlInputRef}
              type="file"
              accept=".xml"
              multiple
              className="hidden"
              onChange={(e) => {
                const xmls = e.target.files ? Array.from(e.target.files) : [];
                if (xmls.length) onImportarExterno(null, xmls);
              }}
            />
            <Button
              type="button"
              variant="outline"
              className="w-full h-10 justify-start font-semibold border-[#0F2D5C] text-[#0F2D5C] bg-white hover:bg-slate-50"
              disabled={busy}
              onClick={() => xmlInputRef.current?.click()}
              data-testid="btn-importar-xml-nomina-confronta"
            >
              <FileCode className="w-4 h-4 mr-2 shrink-0" />
              Seleccionar carpeta/archivos XML de nómina
            </Button>

            <div
              role="button"
              tabIndex={0}
              className={`rounded-md border-2 border-dashed px-3 py-4 text-center text-xs transition-colors ${
                dragOverPdf
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-slate-300 bg-slate-50 hover:border-slate-400"
              }`}
              data-testid="dropzone-pdf-nomina"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverPdf(true);
              }}
              onDragLeave={() => setDragOverPdf(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverPdf(false);
                const pdfs = Array.from(e.dataTransfer.files || []).filter((f) =>
                  f.name.toLowerCase().endsWith(".pdf"),
                );
                if (pdfs.length) onImportarPdfs(pdfs);
              }}
              onClick={() => pdfInputRef.current?.click()}
              onKeyDown={(e) => e.key === "Enter" && pdfInputRef.current?.click()}
            >
              <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400" />
              <p className="font-medium text-slate-700 m-0">Arrastre aquí PDFs de Nómina</p>
              <p className="text-[10px] text-slate-500 mt-1 m-0">RFC, NSS, días e ingresos (extracción inteligente)</p>
            </div>
            <input
              ref={pdfInputRef}
              type="file"
              accept=".pdf"
              multiple
              className="hidden"
              onChange={(e) => onImportarPdfs(e.target.files)}
            />

            <Button type="button" variant="ghost" className="w-full h-9 text-xs" onClick={onOrigenManual}>
              Recuperar historial manual del periodo
            </Button>
          </div>

          {(modoManual || origen === ORIGEN_VISOR) ? (
            <div className="bg-white rounded-md border border-slate-200 p-4 space-y-2">
              <label className="flex items-center gap-2 text-xs text-muted-foreground px-1">
                <input
                  type="checkbox"
                  checked={sincronizarTrabajadores}
                  onChange={(e) => setSincronizarTrabajadores(e.target.checked)}
                  className="rounded border-input"
                />
                Al guardar, sincronizar SDI/días a trabajadores
              </label>
              <Button
                type="button"
                variant="secondary"
                className="w-full h-10 justify-start"
                disabled={busy || !filas.length}
                onClick={onGuardarManual}
                data-testid="btn-guardar-manual-repse"
              >
                <Save className="w-4 h-4 mr-2" />
                Guardar en Historial Manual
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full h-10 justify-start"
                disabled={busy || !filas.length}
                onClick={onSincronizarTrabajadores}
              >
                Sincronizar SDI/días a trabajadores
              </Button>
              <Button type="button" variant="ghost" className="w-full h-9 text-xs" onClick={agregarFila}>
                <Plus className="w-4 h-4 mr-1" /> Agregar fila manual
              </Button>
            </div>
          ) : null}

          {filasMultiContrato.length > 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2">
              <p className="text-xs text-amber-900 m-0">
                {filasMultiContrato.length} trabajador(es) con más de un contrato en el cuatrimestre.
              </p>
              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs justify-start"
                  disabled={busy}
                  onClick={() => onDesglosarMultiContrato("duplicar")}
                >
                  <Split className="w-3.5 h-3.5 mr-1" /> Duplicar fila por contrato
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs justify-start"
                  disabled={busy}
                  onClick={() => onDesglosarMultiContrato("proporcional")}
                >
                  <Split className="w-3.5 h-3.5 mr-1" /> Desglosar días proporcionalmente
                </Button>
              </div>
            </div>
          ) : null}

          <div className="bg-white rounded-md border border-slate-200 p-4 space-y-2">
            <Button
              type="button"
              className="w-full h-11 justify-start bg-[#0F2D5C] hover:bg-[#0a2247] font-semibold"
              disabled={busy || validating || !contratoId || !filas.length}
              onClick={onGenerarSisub}
              data-testid="btn-generar-sisub"
            >
              {busy || validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <span className="mr-2">📄</span>}
              Generar Layout SISUB (IMSS)
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 justify-start font-semibold border-emerald-700 text-emerald-800"
              disabled={busy || validating || !contratoId || !filas.length}
              onClick={onGenerarIcsoe}
              data-testid="btn-generar-icsoe"
            >
              {busy || validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <span className="mr-2">📄</span>}
              Generar Layout ICSOE (INFONAVIT)
            </Button>
          </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="flex flex-col min-h-[420px] bg-white">
          <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-[#0F2D5C] m-0">
                Trabajadores del periodo {anio}-C{cuatrimestre}
                {contratoSel ? ` · Contrato ${contratoSel.numero_contrato}` : ""}
              </p>
              <p className="text-[10px] text-slate-500 m-0">
                {origenLabel} · {filas.length} fila(s)
              </p>
            </div>
            {(busy || validating) ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : null}
          </div>

          {alertaIncompletos ? (
            <div className="mx-4 mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{alertaIncompletos}</span>
            </div>
          ) : null}

          <div className="flex-1 overflow-auto p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px]">NSS</TableHead>
                  <TableHead className="text-[10px]">RFC</TableHead>
                  <TableHead className="text-[10px]">CURP</TableHead>
                  <TableHead className="text-[10px] min-w-[120px]">Nombre</TableHead>
                  <TableHead className="text-[10px]">SDI</TableHead>
                  <TableHead className="text-[10px]">Días</TableHead>
                  <TableHead className="text-[10px]">Reg. pat.</TableHead>
                  <TableHead className="text-[10px]">Días inc.</TableHead>
                  <TableHead className="text-[10px]">Días aus.</TableHead>
                  <TableHead className="text-[10px]">Aport. INF.</TableHead>
                  <TableHead className="text-[10px]">Amort.</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.length ? filas.map((row) => (
                  <TableRow key={row.row_id} className={row.multi_contrato ? "bg-amber-50/50" : ""}>
                    <TableCell className="p-1">{renderCell(row, "nss", "NSS")}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "rfc", "RFC")}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "curp", "CURP")}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "nombre_completo", "Nombre")}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "sdi", "SDI")}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "dias_trabajados", "Días")}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "registro_patronal", "RP")}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "dias_incapacidad", "0", true)}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "dias_ausentismo", "0", true)}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "aportacion_infonavit", "0", true)}</TableCell>
                    <TableCell className="p-1">{renderCell(row, "amortizacion_credito", "0", true)}</TableCell>
                    <TableCell className="p-1">
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => eliminarFila(row.row_id)}>
                        <Trash2 className="w-3.5 h-3.5 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center text-xs text-slate-500 py-10">
                      {visorSatActivo
                        ? "Seleccione contrato REPSE e importe Excel/XML/PDF, o use trabajadores del catálogo asignado."
                        : "Importe Excel, XML o arrastre PDFs de recibos de nómina."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="px-4 py-2 border-t text-[10px] text-slate-500 flex items-center gap-1">
            <FileDown className="w-3.5 h-3.5" />
            Conceptos SUA (incapacidad, ausentismo, INFONAVIT) son editables antes de exportar SISUB/ICSOE.
          </div>
        </div>
      </div>
      </LaboralFlujoOperativo>
    </div>
  );
}
